import SummarizeIcon from '@mui/icons-material/Summarize';
import { Card, CardContent, Chip, Grid, IconButton, TableCell, TableRow, Typography } from '@mui/material';
import { Layout } from 'components/layout';
import BaseTablePaginator from 'components/table/BaseTablePaginator';
import { useDialog } from 'hooks/useDialog';
import { PaginateRequest } from 'interfaces/api-request';
import { Leave } from 'interfaces/leave';
import { Order } from 'interfaces/table';
import { useGetLeaveAdminQuery } from 'lib/api/leaveApi';
import { dateToStringWIB } from 'lib/helpers/dateHelper';
import { leaveStatusToColor, leaveStatusToText } from 'lib/helpers/leaveStatusHelper';
import DetailApprovalDialog from 'modules/leave/components/DetailApprovalDialog';
import { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

const MySwal = withReactContent(Swal);

const AdminLeavePage = () => {
    const [orderBy, setOrderBy] = useState<string>();
    const [order, setOrder] = useState<Order>('asc');
    const [paginateRequest, setPaginateRequest] = useState<PaginateRequest & { work_category_type?: string }>({
        current_page: 0,
        per_page: 10,
        order: order
    });
    useEffect(() => {
        setPaginateRequest({ ...paginateRequest, order: order });
    }, [order]);

    useEffect(() => {
        setPaginateRequest({ ...paginateRequest, order_by: orderBy });
    }, [orderBy]);
    const { isLoading, error, data, isFetching } = useGetLeaveAdminQuery({
        ...paginateRequest,
        current_page: +paginateRequest.current_page! + 1
    });

    const [selectedLeave, setSelectedLeave] = useState<Leave>();

    // Detail Leave Approval Needs
    const { open, handleClose, handleClickOpen } = useDialog();
    const [selectedApprover, setSelectedApprover] = useState<{ title: string; status: string; date: Date; note: string }>();

    return (
        <Layout headerTitle="Leave Request" pageTitle="Leave Request" disableBreadcrumb>
            <Grid container direction={'column'} spacing={20}>
                <Grid item>
                    <Card>
                        <CardContent>
                            <Grid item container justifyContent="flex-end" spacing={8}>
                                {/* <Grid item>
                                    <Button
                                        variant="outlined"
                                        onClick={(val) => {
                                            navigate('/leave/request');
                                        }}
                                    >
                                        Request Leave
                                    </Button>
                                </Grid> */}
                                <Grid item>
                                    <BaseTablePaginator
                                        isLoading={isLoading || isFetching}
                                        count={data?.meta?.total ?? 0}
                                        rowsPerPage={+paginateRequest.per_page!}
                                        page={+paginateRequest.current_page!}
                                        onPageChange={function (
                                            event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
                                            newPage: number
                                        ): void {
                                            setPaginateRequest({ ...paginateRequest, current_page: newPage });
                                        }}
                                        onRowsPerPageChange={function (
                                            event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                                        ): void {
                                            setPaginateRequest({
                                                ...paginateRequest,
                                                current_page: 0,
                                                per_page: parseInt(event.target.value, 10)
                                            });
                                        }}
                                        onSearchchange={function (e: string): void {
                                            setPaginateRequest({ ...paginateRequest, q: e, current_page: 0 });
                                        }}
                                        orderBy={orderBy}
                                        order={order}
                                        onRequestSort={(newOrderBy, newOrder) => {
                                            setOrderBy(newOrderBy);
                                            setOrder(newOrder);
                                            setPaginateRequest({ ...paginateRequest, current_page: 0 });
                                        }}
                                        headCells={[
                                            { id: 'requester', label: 'Requester', isSortable: true },
                                            { id: 'request_date', label: 'Requested Date', isSortable: true },
                                            { id: 'from_date', label: 'From Date', isSortable: true },
                                            { id: 'until_date', label: 'Until Date', isSortable: true },
                                            { id: 'session_type', label: 'Session Type', isSortable: false },
                                            { id: 'approver_1', label: 'Approver 1', isSortable: true },
                                            { id: 'approver_2', label: 'Approver 2', isSortable: true },
                                            { id: 'approver_3', label: 'Approver 3', isSortable: true }
                                        ]}
                                    >
                                        {data?.data?.map((row) => (
                                            <TableRow key={row.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                                <TableCell component="th" scope="row">
                                                    {row.requester?.first_name} {row.requester?.last_name}
                                                </TableCell>
                                                <TableCell component="th" scope="row">
                                                    {dateToStringWIB({ date: row.request_date, withTime: true })}
                                                </TableCell>
                                                <TableCell component="th" scope="row">
                                                    {dateToStringWIB({ date: row.from_date, customFormat: 'YYYY/MM/DD' })}
                                                </TableCell>
                                                <TableCell component="th" scope="row">
                                                    {dateToStringWIB({ date: row.until_date, customFormat: 'YYYY/MM/DD' })}
                                                </TableCell>
                                                <TableCell component="th" scope="row">
                                                    {row.session_type === 'FULL_DAY' ? (
                                                        <Chip label="Full Day" />
                                                    ) : row.session_type === 'HALF_MORNING' ? (
                                                        <Chip label="Half Morning" />
                                                    ) : row.session_type === 'HALF_AFTERNOON' ? (
                                                        <Chip label="Half Afternoon" />
                                                    ) : (
                                                        <Chip label="-" />
                                                    )}
                                                </TableCell>
                                                <TableCell component="th" scope="row">
                                                    <Typography color={leaveStatusToColor(row.approver_1_status)}>
                                                        {leaveStatusToText(row.approver_1_status)}
                                                    </Typography>
                                                    {row.approver1?.first_name} {row.approver1?.last_name}
                                                    {row.approver_1_status != 'P' && (
                                                        <IconButton
                                                            color="primary"
                                                            onClick={() => {
                                                                setSelectedApprover({
                                                                    title: `Approver 1 - ${row.approver1?.first_name}`,
                                                                    status: row.approver_1_status,
                                                                    date: row.approver_1_updated,
                                                                    note: row.approver_1_note
                                                                });
                                                                handleClickOpen();
                                                            }}
                                                        >
                                                            <SummarizeIcon />
                                                        </IconButton>
                                                    )}
                                                </TableCell>
                                                <TableCell component="th" scope="row">
                                                    <Typography color={leaveStatusToColor(row.approver_2_status)}>
                                                        {leaveStatusToText(row.approver_2_status)}
                                                    </Typography>
                                                    {row.approver2?.first_name} {row.approver2?.last_name}
                                                    {row.approver_2_status != 'P' && (
                                                        <IconButton
                                                            color="primary"
                                                            onClick={() => {
                                                                setSelectedApprover({
                                                                    title: `Approver 2 - ${row.approver2?.first_name}`,
                                                                    status: row.approver_2_status,
                                                                    date: row.approver_2_updated,
                                                                    note: row.approver_2_note
                                                                });
                                                                handleClickOpen();
                                                            }}
                                                        >
                                                            <SummarizeIcon />
                                                        </IconButton>
                                                    )}
                                                </TableCell>
                                                <TableCell component="th" scope="row">
                                                    <Typography color={leaveStatusToColor(row.approver_3_status)}>
                                                        {leaveStatusToText(row.approver_3_status)}
                                                    </Typography>
                                                    {row.approver3?.first_name} {row.approver3?.last_name}
                                                    {row.approver_3_status != 'P' && (
                                                        <IconButton
                                                            color="primary"
                                                            onClick={() => {
                                                                setSelectedApprover({
                                                                    title: `Approver 3 - ${row.approver3?.first_name}`,
                                                                    status: row.approver_3_status,
                                                                    date: row.approver_3_updated,
                                                                    note: row.approver_3_note
                                                                });
                                                                handleClickOpen();
                                                            }}
                                                        >
                                                            <SummarizeIcon />
                                                        </IconButton>
                                                    )}
                                                </TableCell>
                                                {/* <TableCell>{row.work_category_type?.name ?? '-'}</TableCell> */}
                                            </TableRow>
                                        ))}
                                    </BaseTablePaginator>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
            <DetailApprovalDialog
                open={open}
                handleClose={() => {
                    handleClose();
                }}
                approverData={selectedApprover}
            />
        </Layout>
    );
};

export default AdminLeavePage;
